import React from 'react';
import { ConfigTemplateCreate } from '../../types/configTemplate';
import { useI18n } from '../../contexts/I18nContext';
import { DeviceTypeSelector } from '../common/DeviceTypeSelector';
import { TemplateImpactPreviewModal } from './TemplateImpactPreviewModal';
import { TemplateOwnerSelect } from './TemplateOwnerSelect';
import { ReplaceDefaultDialog } from './ReplaceDefaultDialog';
import { TemplateFieldsEditor } from './TemplateFieldsEditor';
import { useCreateTemplate } from './createTemplate/useCreateTemplate';

interface CreateConfigTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateCreated: (template: ConfigTemplateCreate) => void;
}

/**
 * A new template: who owns it (ANEP or a client), which gateway it targets,
 * then the values it sets. Every value is optional, a template being a
 * partial overlay on the gateway defaults. The form logic lives in
 * `useCreateTemplate`.
 */
export const CreateConfigTemplateModal: React.FC<CreateConfigTemplateModalProps> = ({ isOpen, onClose, onTemplateCreated }) => {
  const { t } = useI18n();
  const form = useCreateTemplate(isOpen, onClose, onTemplateCreated);
  const { errors, isLoading, deviceType } = form;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={form.close}>
      <div className="config-template-modal tpc" data-testid="modal-create-config-template" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <div className="tpd-title-text">
              <h2>{t('configTemplates.createTitle')}</h2>
              <div className="tpd-title-sub"><span>{t('configTemplates.subtitle')}</span></div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={form.close} aria-label={t('common.close')}>&times;</button>
        </div>

        <form onSubmit={form.submit} noValidate className="tpc-form">
          <div className="modal-content tpd-body">
            {form.error && <div className="error-message" role="alert">{form.error}</div>}

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('configTemplates.create.identity')}</h3>
                <p>{t('configTemplates.create.identityIntro')}</p>
              </header>
              <div className="cu-grid">
                <div className="cu-field cu-field-wide">
                  <label htmlFor="name">{t('configTemplates.templateName')} *</label>
                  <input
                    type="text"
                    id="name"
                    data-testid="field-template-name"
                    value={form.name}
                    onChange={(e) => form.setName(e.target.value)}
                    placeholder={t('configTemplates.namePlaceholder')}
                    className={`cu-input${errors.name ? ' is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.name && <span className="cu-error">{errors.name}</span>}
                </div>

                <div className="cu-field cu-field-wide">
                  <label htmlFor="description">{t('common.description')}</label>
                  <textarea
                    id="description"
                    data-testid="field-template-description"
                    className="cu-input"
                    value={form.description}
                    onChange={(e) => form.setDescription(e.target.value)}
                    placeholder={t('configTemplates.descriptionPlaceholder')}
                    rows={2}
                    disabled={isLoading}
                  />
                </div>

                <div className="cu-field">
                  <label htmlFor="deviceType">{t('devices.deviceType')} *</label>
                  <DeviceTypeSelector
                    id="deviceType"
                    value={deviceType}
                    onChange={(value) => { form.setDeviceType(value); form.clearError('device_type'); }}
                    placeholder={t('configTemplates.selectHardwareTypePlaceholder')}
                    includeEmpty
                    className={`cu-input${errors.device_type ? ' is-invalid' : ''}`}
                    disabled={isLoading}
                  />
                  {errors.device_type && <span className="cu-error">{errors.device_type}</span>}
                </div>

                <div className="cu-field">
                  <label htmlFor="templateOwner">{t('configTemplates.ownerLabel')} *</label>
                  <TemplateOwnerSelect
                    id="templateOwner"
                    value={form.ownerId}
                    onChange={(next) => { form.setOwnerId(next); form.clearError('client_id'); }}
                    error={!!errors.client_id}
                    disabled={isLoading}
                  />
                  {errors.client_id ? <span className="cu-error">{errors.client_id}</span> : <span className="cu-hint">{t('configTemplates.ownerHint')}</span>}
                </div>

                <div className="cu-field cu-field-wide">
                  <label className="cu-switch tpd-switch">
                    <input type="checkbox" data-testid="field-template-default" checked={form.isDefault} onChange={(e) => form.setIsDefault(e.target.checked)} disabled={isLoading} />
                    <span className="cu-switch-track" aria-hidden="true"><span className="cu-switch-thumb" /></span>
                    <span className="cu-switch-text">
                      <strong>{t('configTemplates.defaultTemplate')}</strong>
                      <span className="cu-hint">{t('configTemplates.defaultHint')}</span>
                    </span>
                  </label>
                </div>
              </div>
            </section>

            <section className="cu-section">
              <header className="cu-section-head">
                <h3>{t('configTemplates.create.fieldsTitle')}{deviceType && <span className="type-badge tpl-type tpc-head-type">{deviceType}</span>}</h3>
                <p>{t('configTemplates.layersHint')}</p>
              </header>

              {!deviceType ? (
                <div className="tpd-empty" data-testid="create-template-pick-type"><p>{t('configTemplates.create.selectTypeFirst')}</p></div>
              ) : form.schemaLoading ? (
                <div className="tpd-empty"><p>{t('configTemplates.detail.schemaLoading', { type: deviceType })}</p></div>
              ) : form.schema && form.schema.length === 0 ? (
                <div className="tpd-empty">
                  <p><strong>{t('configTemplates.create.noFields', { type: deviceType })}</strong></p>
                  <p>{t('configTemplates.create.noFieldsHint')}</p>
                </div>
              ) : (
                <TemplateFieldsEditor
                  groups={form.groupedSchema}
                  values={form.fieldValues}
                  errors={errors}
                  disabled={isLoading}
                  onChange={form.setFieldValue}
                  password={form.passwordInSchema ? {
                    generate: form.generatePassword,
                    value: form.password,
                    placeholder: t('configTemplates.leaveBlankToNotOverride'),
                    onGenerateChange: (generate) => { form.setGeneratePassword(generate); if (generate) form.setPassword(''); },
                    onValueChange: form.setPassword,
                  } : undefined}
                />
              )}
            </section>
          </div>

          <div className="modal-actions tpd-footer tpc-footer">
            <div className="tpc-summary" data-testid="create-template-summary">
              {form.summaryReady ? (
                <>
                  <strong>{form.name.trim()}</strong>
                  <span className="type-badge tpl-type">{deviceType}</span>
                  {form.ownerName && <span className="tpc-summary-owner">{form.ownerName}</span>}
                  {form.setCount > 0 && <span className="tpc-summary-count">{form.setCount}</span>}
                </>
              ) : (
                <span className="tpd-muted">{t('configTemplates.create.summaryEmpty')}</span>
              )}
            </div>
            <div className="tpc-actions">
              <button type="button" className="btn btn-secondary" data-testid="btn-cancel" onClick={form.close} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-secondary" data-testid="btn-preview-impact" onClick={form.openPreview} disabled={isLoading} title={t('templateImpact.subtitle')}>
                {t('templateImpact.previewButton')}
              </button>
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isLoading}>
                {isLoading ? t('configTemplates.create.creating') : t('common.create')}
              </button>
            </div>
          </div>
        </form>
      </div>

      <TemplateImpactPreviewModal
        isOpen={form.showPreview}
        onClose={() => form.setShowPreview(false)}
        onConfirm={form.confirmFromPreview}
        confirming={isLoading}
        request={form.previewRequest}
      />

      <ReplaceDefaultDialog
        existing={form.defaultConflict?.existing ?? null}
        ownerName={form.ownerName}
        deviceType={deviceType}
        newName={form.name.trim()}
        busy={isLoading}
        onReplace={() => form.resolveConflict(true)}
        onSaveWithoutDefault={() => form.resolveConflict(false)}
        onCancel={() => form.resolveConflict(null)}
      />
    </div>
  );
};
